import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import './PurchaseAffinitiesModal.scss';
import ModalWithConfirmationButtons from '../../../../../common/Modal/ModalWithConfirmationButtons/ModalWithConfirmationButtons';
import MultiLevelTableSelector from '../../../../../common/MultiLevelTableSelector/MultiLevelTableSelector';
import { fetchAudienceCategories } from '../../../../../../services/AudienceServiceHelper';
import {
  buildPurchaseAffinitiesTree,
  collectSelectedCategories,
} from '../../../../../../utils/purchaseAffinitiesTreeUtils';

const SEGMENT_TYPE = 'purchaseAffinities';
// Labels are unused while the level selector is hidden, but the prop is required.
const LEVEL_NAMES = ['Categories', 'Subcategories', 'Segments'];

/**
 * Audience Builder modal for the Purchase Affinities segment. Renders the 3-level
 * purchase taxonomy as a collapsible tree; parent selects children; partial parents show "-".
 * Submits every checked node (parents included) as { id, path }.
 */
const PurchaseAffinitiesModal = ({ isOpen, modalTitle, purchaseAffinitiesInput = {}, onSubmit, onCancel }) => {
  const [tree, setTree] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const storedIds = useMemo(
    () => (purchaseAffinitiesInput.categories || []).map((c) => String(c.id)),
    [purchaseAffinitiesInput.categories]
  );

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    fetchAudienceCategories('purchase')
      .then((categoriesMap) => {
        if (!isMounted) return;
        const { tree: builtTree, missingIds } = buildPurchaseAffinitiesTree(categoriesMap, storedIds);
        if (missingIds.length)
          console.warn(`PurchaseAffinitiesModal: dropped ids missing from taxonomy: ${missingIds.join(', ')}`);
        setTree(builtTree);
        setIsLoading(false);
      })
      .catch(() => {
        if (!isMounted) return;
        setTree([]);
        setIsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [storedIds]);

  const selectedCategories = useMemo(() => collectSelectedCategories(tree), [tree]);

  const handleSubmit = () =>
    onSubmit({ ...purchaseAffinitiesInput, type: SEGMENT_TYPE, categories: selectedCategories });
  const handleCancel = () => onCancel(SEGMENT_TYPE);

  return (
    <div className="purchase-affinities-modal-component" data-test-id="purchase-affinities-modal">
      <ModalWithConfirmationButtons
        width="810px"
        modalTitle={modalTitle}
        isOpen={isOpen}
        isSubmitDisabled={isLoading || selectedCategories.length === 0}
        isDisabledEnterKeyPress={isLoading || selectedCategories.length === 0}
        showSpinner={isLoading}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onXClick={handleCancel}
        isLightTheme={true}
      >
        <div className="purchase-affinities-modal-content">
          {/* Mount the tree only once the taxonomy is in: the selector shows its own
              "No content to display." empty state for an empty tableData, which must not
              appear behind the loading spinner. */}
          {!isLoading && (
            <MultiLevelTableSelector
              levelNames={LEVEL_NAMES}
              tableData={tree}
              onChange={(updated) => setTree([...updated])}
              showLevelSelector={false}
              initialLevelName="All"
              searchPlaceholder="Search Categories"
              listLimit={500}
            />
          )}
        </div>
      </ModalWithConfirmationButtons>
    </div>
  );
};

PurchaseAffinitiesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  modalTitle: PropTypes.string,
  purchaseAffinitiesInput: PropTypes.shape({
    index: PropTypes.number,
    categories: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, path: PropTypes.arrayOf(PropTypes.string) })),
  }),
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default PurchaseAffinitiesModal;
